import { HydrationError } from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { PropertyPrefixes } from 'src/features/flows/types/flowParameter.types';
import { Tag } from 'src/shared/types/model.types';

export const SIDE_NAV_WIDTH = 244;

export const displaySimulateMode = {
  all: 'Full Flow',
  safe: 'Safe Actions Only',
  custom: 'Custom Selection',
};

export const hydrationErrorIcon: Record<HydrationError['severity'], { icon: Icons; color: string; bgColor: string }> = {
  warning: {
    icon: 'Wrench',
    color: 'text-orange-50',
    bgColor: 'bg-orange-500',
  },
  error: {
    icon: 'TriangleAlert',
    color: 'text-destructive-100',
    bgColor: 'bg-destructive/90',
  },
  fatal: {
    icon: 'OctagonX',
    color: 'text-destructive-100',
    bgColor: 'bg-destructive/90',
  },
};

export const placeholdersPerType = {
  string: 'Enter a value',
  number: 'Enter a number',
  email: 'john@tekst.com',
  date: '24/12/2024',
};

export const tagStatus: Record<Tag['status'], { color: string; label: string }> = {
  ACTIVE: { color: 'text-success-800 bg-success-100/90', label: 'Active' },
  REMOVED: { color: 'text-destructive bg-destructive-100/90', label: 'Removed' },
  PENDING_ADDED: { color: 'text-warning-800 bg-warning-100/90', label: 'Addition Pending' },
  PENDING_UPDATED: { color: 'text-warning-800 bg-warning-100/90', label: 'Update Pending' },
  PENDING_REMOVED: { color: 'text-warning-800 bg-warning-100/90', label: 'Removal Pending' },
  SUGGESTED: { color: 'text-purple-600 bg-purple-100/90', label: 'Suggested' },
};

export const EMAIL_REGEX = /[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export const publishTypes = [
  { type: 'major', label: 'Major', description: 'Breaking changes that are not backwards compatible' },
  { type: 'minor', label: 'Minor', description: 'New features that are backwards compatible' },
  { type: 'patch', label: 'Patch', description: 'Bug fixes and minor updates' },
];

export const dependencyTypes = [
  {
    type: 'dependency',
    label: 'Dependency',
    icon: 'Box',
    description: 'Runtime dependencies required for production',
  },
  {
    type: 'devDependency',
    label: 'Dev Dependency',
    icon: 'Code',
    description: 'Dependencies only needed during development',
  },
];

// this has to be in sync with the conversion functions
export const conditionCategories: Record<PropertyPrefixes, string> = {
  thread: 'Message',
  facet: 'Tags',
  custom: 'Custom',
};

export const identityProviders = {
  google: {
    shortLabel: 'Google',
    label: 'Google Identity Cloud',
    description: 'Enterprise-grade identity and access management from Google',
    image: '/static/images/idp/google_ic.png',
    supportArticle: 'https://support.tekst.com/hc/en-us/articles/14985366523922-Using-Google-for-SSO-with-Tekst',
  },
  microsoft: {
    shortLabel: 'Microsoft',
    label: 'Microsoft Azure AD',
    description: 'Microsoft identity services, Azure AD or Entra ID',
    image: '/static/images/idp/microsoft.png',
    supportArticle: 'https://support.tekst.com/hc/en-us/articles/14985358708370-Using-Azure-AD-for-SSO-with-Tekst',
  },
  okta: {
    shortLabel: 'Okta',
    label: 'Okta',
    description: 'Cloud-first identity and access management platform',
    image: '/static/images/idp/okta.png',
    supportArticle: 'https://support.tekst.com/hc/en-us/articles/14985350606098-Using-Okta-for-SSO-with-Tekst',
  },
  forgeRock: {
    shortLabel: 'ForgeRock',
    label: 'ForgeRock',
    description: 'Modern identity platform for digital experiences',
    image: '/static/images/idp/forgerock.png',
    supportArticle: null,
  },
  PingIdentity: {
    shortLabel: 'Ping Identity',
    label: 'Ping Identity',
    description: 'Enterprise identity security solutions',
    image: '/static/images/idp/ping.png',
    supportArticle: null,
  },
  oneLogin: {
    shortLabel: 'One Login',
    label: 'One Login',
    description: 'Unified access management for cloud and enterprise',
    image: '/static/images/idp/onelogin.png',
    supportArticle: null,
  },
} as const;
